/* eslint-disable no-nested-ternary */
import React from "react";

export enum TagColor {
  white = "white",
  pink = "pink",
  green = "green",
  blue = "blue",
  amber = "amber",
  purple = "purple",
  red = "red",
  gray = "gray",
}

export enum TagType {
  dotted = "dotted",
  label = "label",
}

export enum TagPosition {
  topRight = "topRight",
  topLeft = "topLeft",
  bottomRight = "bottomRight",
  bottomLeft = "bottomLeft",
}

const backgroundColors: Record<TagColor, string> = {
  white: "bg-badge-white-icon",
  pink: "bg-badge-pink-icon",
  green: "bg-badge-green-icon",
  blue: "bg-badge-blue-icon",
  amber: "bg-badge-amber-icon",
  purple: "bg-badge-purple-icon",
  red: "bg-badge-red-icon",
  gray: "bg-badge-gray-icon",
};

const sizes: Record<TagType, string> = {
  [TagType.dotted]: "w-3 h-3 min-w-3 min-h-3",
  [TagType.label]: "w-5 h-5 min-w-5 min-h-5",
};

// Uses the tailwind theme value to get the sizes of the tag sizes above.
const positions: Record<TagType, Record<TagPosition, string>> = {
  [TagType.dotted]: {
    [TagPosition.topRight]: "-top-[calc(theme(space.3)/2)] -right-[calc(theme(space.3)/2)]",
    [TagPosition.topLeft]: "-top-[calc(theme(space.3)/2)] -left-[calc(theme(space.3)/2)]",
    [TagPosition.bottomRight]: "top-[calc(100%-theme(space.3)/2)] -right-[calc(theme(space.3)/2)]",
    [TagPosition.bottomLeft]: "top-[calc(100%-theme(space.3)/2)] -left-[calc(theme(space.3)/2)]",
  },

  [TagType.label]: {
    [TagPosition.topRight]: "-top-[calc(theme(space.5)/2)] -right-[calc(theme(space.5)/2)]",
    [TagPosition.topLeft]: "-top-[calc(theme(space.5)/2)] -left-[calc(theme(space.5)/2)]",
    [TagPosition.bottomRight]: "top-[calc(100%-theme(space.5/2))] -right-[calc(theme(space.5)/2)]",
    [TagPosition.bottomLeft]: "top-[calc(100%-theme(space.5/2))] -left-[calc(theme(space.5)/2)]",
  },
};

export type TagProps = {
  /** If a boolean, type is ignored and a dot is displayed */
  value?: boolean | number;
  type?: TagType | `${TagType}`;
  color?: TagColor | `${TagColor}`;
  /** Max count before showing a plus. Default: 9 */
  max?: number;
  position?: TagPosition | `${TagPosition}`;
}

const Tag: React.FC<React.PropsWithChildren<TagProps>> = ({
  value,
  type = TagType.label,
  color = TagColor.blue,
  max = 9,
  children,
  position = TagPosition.topRight,
}) => {
  const resolvedType = typeof value === "boolean" || type === TagType.dotted ? TagType.dotted : TagType.label;

  // Standalone
  if (React.Children.count(children) < 1) {
    return (
      <div className={`text-center ${backgroundColors[color]} ${sizes[resolvedType]}  text-sm text-text-on-color rounded-full`}>
        {typeof value === "number" ? value > max ? `${max}+` : value : ""}
      </div>
    );
  }

  return (
    <>
      <div className="relative w-fit">
        {children}
        {!!value && (
          <div
            className={`absolute text-center ${sizes[resolvedType]} ${positions[resolvedType][position]} ${backgroundColors[color || TagColor.white]} text-sm text-text-on-color rounded-full`}
          >
            {typeof value === "number" ? value > max ? `${max}+` : value : ""}
          </div>
        )}
      </div>
    </>
  );
};

export default Tag;
